import { filterDescriptiveValues } from "utils/insight/data";
import { humanize, LabelFactory } from "utils/insight/metadata";
import { ChartAxisType, ChartSeriesType } from "utils/insight/types";
import { Summary } from "utils/insight/summary";
import { conditionsIsEmpty } from "./conditions";
export class InsightChartFactoryProvider {
    typeFactoryMap;
    constructor(typeFactoryMap) {
        this.typeFactoryMap = typeFactoryMap;
    }
    getChartFactory(type) {
        return this.typeFactoryMap[type];
    }
    static create(metadata, minValueCount) {
        const labelFactory = LabelFactory.create(metadata);
        const desc = new InsightChartFactoryDescriptive(labelFactory, minValueCount);
        const map = {
            ["ModelDriver"]: new InsightChartFactoryModelDriver(labelFactory),
            ["Correlation"]: new InsightChartFactoryCorrelation(desc),
            ["Descriptive"]: desc,
            ["Difference"]: new InsightChartFactoryDifference(labelFactory, 0 /* disable for whats changed */)
        };
        return new InsightChartFactoryProvider(map);
    }
}
class InsightChartFactoryDifference {
    labelFactory;
    minValueCount;
    constructor(labelFactory, minValueCount) {
        this.labelFactory = labelFactory;
        this.minValueCount = minValueCount;
    }
    getChart(insight) {
        if (insight.conditions !== undefined && !conditionsIsEmpty(insight.conditions)) {
            return this.getChartWithConditions(insight);
        }
        const xAxisId = insight.id;
        const xAxis = {
            dataKey: "name",
            label: this.labelFactory.getColumnLabel(xAxisId),
            type: this.labelFactory.getColumnChartAxisType(xAxisId)
        };
        const yAxis = {
            label: this.labelFactory.getOutcomeLabel(),
            unit: this.labelFactory.getUnit()
        };
        const data = filterDescriptiveValues(this.labelFactory.metadata, insight, this.minValueCount).map(({ summary }) => ({
            name: summary.conditions.map((c) => this.labelFactory.getRawValue(c.id, c.value))[0],
            label: this.labelFactory.getSetLabel(summary.conditions),
            y: this.labelFactory.formatOutcomeValue(summary),
            summary: summary
        }));
        if (data.length < insight.values.length) {
            const otherValues = insight.values.filter(({ summary }) => summary.getTotalN() < this.minValueCount);
            const otherSummary = Summary.addAll(otherValues.map((d) => d.summary));
            data.push({
                name: "{Other}",
                label: `All Other ${this.labelFactory.getColumnLabel(insight.id)}`,
                y: this.labelFactory.formatOutcomeValue(otherSummary),
                summary: otherSummary,
                otherSummary: insight.globalSummary.subtract(otherSummary)
            });
        }
        const series = [
            {
                type: this.labelFactory.getColumnChartSeriesType(xAxisId),
                dataKey: "y",
                labelKey: "label",
                unit: this.labelFactory.getUnit()
            }
        ];
        return {
            xAxis: xAxis,
            yAxis: yAxis,
            data: data,
            series: series
        };
    }
    getChartWithConditions(insight) {
        const xAxis = {
            dataKey: "name",
            label: this.labelFactory.getColumnLabel(insight.id),
            type: this.labelFactory.getColumnChartAxisType(insight.id)
        };
        const yAxis = {
            // label: this.labelFactory.getOutcomeLabel(),
            dataKey: "y",
            label: `% of Total ${this.labelFactory.getOutcomeLabel()}`,
            unit: this.labelFactory.getUnit()
        };
        const data = filterDescriptiveValues(this.labelFactory.metadata, insight, this.minValueCount).map(({ conditions, constraint, summary, other }) => {
            const yCount = summary?.getTotalN();
            const otherYCount = other?.getTotalN() + yCount;
            const allWins = (summary?.sum || 0) + (other?.sum || 0);
            const y = Number(((summary?.sum / (allWins || 1)) * 100).toFixed(2));
            const otherY = Number(((yCount / (otherYCount || 1)) * 100).toFixed(2));
            return {
                name: this.labelFactory.getRawValues(conditions)[0],
                label: `${this.labelFactory.getSetLabel(constraint)}`,
                otherLabel: `% of Total Count`,
                y: y,
                otherY: otherY,
                yCount: yCount,
                otherYCount: otherYCount,
                summary: summary,
                otherSummary: other
            };
        });
        if (data.length < insight.values.length) {
            const otherValues = insight.values.filter(({ summary }) => summary.getTotalN() < this.minValueCount);
            const summary = Summary.addAll(otherValues.map((d) => d.summary));
            const other = Summary.addAll(otherValues.map((d) => d.other));
            const setLabel = `All Other ${this.labelFactory.getColumnLabel(insight.id)}`;
            const otherSetLabel = this.labelFactory.getOtherSetLabel(insight.conditions);
            data.push({
                name: "{Other}",
                label: `${setLabel} when ${this.labelFactory.getSetLabel(insight.conditions)}`,
                otherLabel: `${setLabel} ${otherSetLabel.startsWith("all") ? "for" : "when"} ${otherSetLabel}`,
                y: this.labelFactory.formatOutcomeValue(summary),
                otherY: this.labelFactory.formatOutcomeValue(other),
                yCount: summary?.getTotalN(),
                otherYCount: other?.getTotalN(),
                summary: summary,
                otherSummary: other
            });
        }
        const series = [
            {
                type: ChartSeriesType.Line,
                dataKey: "y",
                labelKey: "label",
                label: `% of Total ${this.labelFactory.getOutcomeLabel()}`,
                unit: this.labelFactory.getUnit()
            },
            {
                type: ChartSeriesType.Line,
                dataKey: "otherY",
                labelKey: "otherLabel",
                label: "% of Total Count",
                colorFormatter: () => "#999",
                unit: this.labelFactory.getUnit()
            }
        ];
        return {
            xAxis: xAxis,
            yAxis: yAxis,
            data: data,
            series: series
        };
    }
}
class InsightChartFactoryDescriptive {
    labelFactory;
    minValueCount;
    constructor(labelFactory, minValueCount) {
        this.labelFactory = labelFactory;
        this.minValueCount = minValueCount;
    }
    getChart(insight) {
        if (insight.conditions !== undefined && !conditionsIsEmpty(insight.conditions)) {
            return this.getChartWithConditions(insight);
        }
        const xAxisId = insight.id;
        const xAxis = {
            dataKey: "name",
            label: this.labelFactory.getColumnLabel(xAxisId),
            type: this.labelFactory.getColumnChartAxisType(xAxisId)
        };
        const yAxis = {
            label: this.labelFactory.getOutcomeLabel(),
            unit: this.labelFactory.getUnit()
        };
        const data = filterDescriptiveValues(this.labelFactory.metadata, insight, this.minValueCount).map(({ summary }) => ({
            name: summary.conditions.map((c) => this.labelFactory.getRawValue(c.id, c.value))[0],
            label: this.labelFactory.getSetLabel(summary.conditions),
            y: this.labelFactory.formatOutcomeValue(summary),
            summary: summary,
            otherSummary: insight.globalSummary.subtract(summary)
        }));
        if (data.length < insight.values.length) {
            const otherValues = insight.values.filter(({ summary }) => summary.getTotalN() < this.minValueCount);
            const otherSummary = Summary.addAll(otherValues.map((d) => d.summary));
            data.push({
                name: "{Other}",
                label: `All Other ${this.labelFactory.getColumnLabel(insight.id)}`,
                y: this.labelFactory.formatOutcomeValue(otherSummary),
                summary: otherSummary,
                otherSummary: insight.globalSummary.subtract(otherSummary)
            });
        }
        const series = [
            {
                type: this.labelFactory.getColumnChartSeriesType(xAxisId),
                dataKey: "y",
                labelKey: "label",
                unit: this.labelFactory.getUnit()
            }
        ];
        return {
            xAxis: xAxis,
            yAxis: yAxis,
            data: data,
            series: series
        };
    }
    getChartWithConditions(insight) {
        const xAxis = {
            dataKey: "name",
            label: this.labelFactory.getColumnLabel(insight.id),
            type: this.labelFactory.getColumnChartAxisType(insight.id)
        };
        const yAxis = {
            label: this.labelFactory.getOutcomeLabel(),
            unit: this.labelFactory.getUnit()
        };
        const data = filterDescriptiveValues(this.labelFactory.metadata, insight, this.minValueCount).map(({ conditions, constraint, summary, other }) => {
            const setLabel = this.labelFactory.getSetLabel(conditions);
            const otherSetLabel = this.labelFactory.getOtherSetLabel(constraint);
            return {
                name: this.labelFactory.getRawValues(conditions)[0],
                label: `${setLabel} when ${this.labelFactory.getSetLabel(constraint)}`,
                otherLabel: `${setLabel} ${otherSetLabel.startsWith("all") ? "for" : "when"} ${otherSetLabel}`,
                y: this.labelFactory.formatOutcomeValue(summary),
                otherY: this.labelFactory.formatOutcomeValue(other),
                yCount: summary?.getTotalN(),
                otherYCount: other?.getTotalN(),
                summary: summary,
                otherSummary: other
            };
        });
        if (data.length < insight.values.length) {
            const otherValues = insight.values.filter(({ summary }) => summary.getTotalN() < this.minValueCount);
            const summary = Summary.addAll(otherValues.map((d) => d.summary));
            const other = Summary.addAll(otherValues.map((d) => d.other));
            const setLabel = `All Other ${this.labelFactory.getColumnLabel(insight.id)}`;
            const otherSetLabel = this.labelFactory.getOtherSetLabel(insight.conditions);
            data.push({
                name: "{Other}",
                label: `${setLabel} when ${this.labelFactory.getSetLabel(insight.conditions)}`,
                otherLabel: `${setLabel} ${otherSetLabel.startsWith("all") ? "for" : "when"} ${otherSetLabel}`,
                y: this.labelFactory.formatOutcomeValue(summary),
                otherY: this.labelFactory.formatOutcomeValue(other),
                yCount: summary?.getTotalN(),
                otherYCount: other?.getTotalN(),
                summary: summary,
                otherSummary: other
            });
        }
        const series = [
            {
                type: this.labelFactory.getColumnChartSeriesType(insight.id),
                dataKey: "y",
                labelKey: "label",
                unit: this.labelFactory.getUnit()
            },
            {
                type: this.labelFactory.getColumnChartSeriesType(insight.id),
                dataKey: "otherY",
                labelKey: "otherLabel",
                colorFormatter: () => "#999",
                unit: this.labelFactory.getUnit()
            }
        ];
        return {
            xAxis: xAxis,
            yAxis: yAxis,
            data: data,
            series: series
        };
    }
}
class InsightChartFactoryCorrelation {
    delegate;
    constructor(delegate) {
        this.delegate = delegate;
    }
    getChart(insight) {
        return this.delegate.getChart(insight);
    }
}
class InsightChartFactoryModelDriver {
    labelFactory;
    constructor(labelFactory) {
        this.labelFactory = labelFactory;
    }
    getChart(insight) {
        if (!conditionsIsEmpty(insight.conditions)) {
            return this.getSingleModelDriverChart(insight);
        }
        const xAxis = {
            dataKey: "x_name",
            label: this.labelFactory.getColumnLabel(insight.id)
        };
        const yAxis = {
            dataKey: "y_name",
            label: "Model"
        };
        const data = insight.models.flatMap((model) => model.features.map((feature) => ({
            x_name: this.labelFactory.getValueLabels(feature.conditions),
            y_name: model.modelName,
            y: feature.value,
            scaled_y: feature.value / insight.scale
        })));
        const series = [
            {
                type: ChartSeriesType.HeatMap,
                dataKey: "y"
            }
        ];
        return {
            xAxis: xAxis,
            yAxis: yAxis,
            data: data,
            series: series
        };
    }
    getSingleModelDriverChart(insight) {
        const xAxis = {
            type: ChartAxisType.Number,
            label: "Percentage Delta",
            dataKey: "y",
            unit: "%"
        };
        const yAxis = {
            type: ChartAxisType.Category,
            label: "Model",
            dataKey: "name"
        };
        const data = insight.models.flatMap((model) => model.features.map((features) => ({
            name: model.modelName,
            y: Number(humanize(features.value * 100, 2))
        })));
        const series = [
            {
                type: ChartSeriesType.Bar,
                dataKey: "y",
                labelKey: "name",
                unit: "%"
            }
        ];
        return {
            xAxis: xAxis,
            yAxis: yAxis,
            data: data,
            series: series,
            layout: "vertical"
        };
    }
}
